/**
 * settings_xero_controller.js — Xero integration panel front-end glue (Stimulus).
 *
 * Ports static/js/settings_xero.js to an element-scoped controller.
 *
 * Backend: /settings/xero/* (session-auth bridge over /api/integrations/xero/*).
 * Markup:  templates/settings/_integrations.html (id='xero-panel').
 *
 * Handlers are registered on `window` so global-actions.js can look them
 * up via `window[el.dataset.fn]` from `data-action="call-fn"` buttons.
 *
 * Lifecycle (replaces the former attachOnce('settingsXeroInit') once-guard):
 *   connect()    runs the page init (initXeroPanel) that the old code ran once
 *                via attachOnce and then re-ran from a turbo:load callback.
 *                connect() runs on every navigation that inserts the panel, so it
 *                subsumes both. There are no document-delegated listeners to bind
 *                here — all interactive wiring is via window.* handlers dispatched
 *                from data-action="call-fn" buttons. connect() kicks off the
 *                status fetch and opens the bulk-sync progress stream.
 *   disconnect() closes the progress stream so it doesn't survive the panel's
 *                removal.
 *
 * Module-private state (bulkStream, bulkState, pendingSyncRetry) and the
 * HTTP/render helpers live in the IIFE closure — they survive Turbo's <body>
 * swap on the persistent module scope, which is why initXeroPanel clears a
 * stale bulkState before re-attaching.
 *
 * Non-blocking bulk sync: /settings/xero/sync/all returns 202 after
 * claiming a sync slot on XeroConnection; the actual work runs in a
 * background thread. Progress persists in DB so it survives page reloads and
 * multi-tab use. The worker pushes 'xero_bulk_progress' events (the /status
 * payload) to the tenant's private Mercure topic; the client renders them and
 * detects completion on the bulk_sync_in_progress true→false transition. No
 * polling: a single reconcile GET runs per stream (re)connect to close the
 * sync-completed-while-disconnected gap.
 *
 * data-controller="settings-xero" hosts on #xero-panel.
 */

import { Controller } from "@hotwired/stimulus";

    // ------------------------------------------------------------------------
    // HTTP helpers
    // ------------------------------------------------------------------------

    async function postJSON(url, body) {
        const resp = await window.api(url, {
            method: 'POST',
            body: body == null ? {} : body,
            responseType: 'raw',
            throwOnError: false,
            notifyError: false
        });
        let data = {};
        try { data = await resp.json(); } catch (_e) { /* non-JSON */ }
        return {ok: resp.ok, status: resp.status, data: data};
    }

    async function getJSON(url) {
        const resp = await window.api(url, {
            responseType: 'raw',
            throwOnError: false,
            notifyError: false
        });
        let data = {};
        try { data = await resp.json(); } catch (_e) { /* non-JSON */ }
        return {ok: resp.ok, status: resp.status, data: data};
    }

    // ------------------------------------------------------------------------
    // Panel state rendering
    // ------------------------------------------------------------------------

    function show(id) { const el = document.getElementById(id); if (el) el.hidden = false; }
    function hide(id) { const el = document.getElementById(id); if (el) el.hidden = true; }
    function setText(id, value) { const el = document.getElementById(id); if (el) el.textContent = value; }
    // base.html stamps this on <html> from the server's is_native_shell().
    function isNativeShell() { return document.documentElement.hasAttribute('data-hotwire-native'); }

    function relativeTime(iso) {
        if (!iso) return 'never';
        const then = new Date(iso);
        if (isNaN(then.getTime())) return 'never';
        const diffMs = Date.now() - then.getTime();
        const sec = Math.round(diffMs / 1000);
        if (sec < 60) return sec + 's ago';
        const min = Math.round(sec / 60);
        if (min < 60) return min + 'm ago';
        const hr = Math.round(min / 60);
        if (hr < 24) return hr + 'h ago';
        const day = Math.round(hr / 24);
        return day + 'd ago';
    }

    function statusBadge(status) {
        if (status === 'success') {
            return '<span class="badge bg-success-subtle text-success"><i class="bi bi-check-circle"></i> success</span>';
        }
        if (status === 'error') {
            return '<span class="badge bg-danger-subtle text-danger"><i class="bi bi-x-circle"></i> error</span>';
        }
        if (status === 'skipped') {
            return '<span class="badge bg-secondary-subtle text-secondary"><i class="bi bi-dash-circle"></i> skipped</span>';
        }
        return '<span class="badge bg-secondary">' + (status || '?') + '</span>';
    }

    function escapeHtml(s) {
        if (s == null) return '';
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function renderLogs(logs) {
        const tbody = document.getElementById('xero-sync-log-body');
        const empty = document.getElementById('xero-sync-log-empty');
        if (!tbody) return;
        if (!logs || logs.length === 0) {
            tbody.innerHTML = '';
            if (empty) empty.hidden = false;
            return;
        }
        if (empty) empty.hidden = true;
        tbody.innerHTML = logs.map(function (log) {
            const entity = escapeHtml(log.entity_type) + '#' + escapeHtml(log.entity_id);
            const action = escapeHtml(log.action || log.sync_action || '');
            const status = statusBadge(log.status || log.sync_status);
            const when = escapeHtml(relativeTime(log.created_at));
            // Render the whole message: a name-conflict error carries its remedy
            // (rename or Merge Duplicate) at the end, so cutting it hides the fix.
            const errMsg = log.error_message ? escapeHtml(String(log.error_message)) : '';
            return '<tr><td>' + entity + '</td><td>' + action + '</td><td>' + status +
                   '</td><td>' + when + '</td><td class="text-danger small text-break">' + errMsg + '</td></tr>';
        }).join('');
    }

    function renderDisconnectedState(status) {
        const state = status.state || 'never_connected';
        const banner = document.getElementById('xero-disconnected-banner');
        const blurb = document.getElementById('xero-disconnected-blurb');
        const label = document.getElementById('xero-connect-label');
        const button = document.getElementById('xero-connect-btn');
        if (!banner || !blurb || !label || !button) return;

        // A plan without Xero keeps the button (it prompts to upgrade on click)
        // but greys it out and says which plans include Xero. The native shells
        // never say "upgrade" (App Store 3.1.1 / Google Play Payments).
        const included = status.plan_includes_xero !== false;
        button.classList.toggle('btn-primary', included);
        button.classList.toggle('btn-outline-secondary', !included);
        if (!included) {
            banner.hidden = true;
            banner.innerHTML = '';
            blurb.textContent = status.upgrade_message;
            label.textContent = isNativeShell() ? 'Connect Xero' : 'Upgrade to connect Xero';
            return;
        }

        if (state === 'never_connected') {
            banner.hidden = true;
            banner.innerHTML = '';
            blurb.textContent = 'Connect your Xero account to push clients and invoices to your accounting system. Payment status updates flow back via webhooks.';
            label.textContent = 'Connect Xero';
            return;
        }

        if (state === 'disconnected') {
            banner.hidden = true;
            banner.innerHTML = '';
            blurb.textContent = 'Xero is disconnected. Reconnect to resume sync.';
            label.textContent = 'Reconnect Xero';
            return;
        }

        // needs_reauth — show yellow banner with reason-specific copy
        const reason = status.deactivation_reason || 'unknown_auth_error';
        const msgByReason = {
            refresh_token_expired: 'Your Xero session expired. Click Reconnect to restore sync.',
            subscription_inactive: 'Your Xero subscription appears inactive (cancelled/expired/deleted). Reactivate it in Xero, then reconnect.',
            user_removed: 'Your Xero user role changed — the original connecting user no longer has access. Reconnect to re-authorise.',
            app_disconnected_by_xero: 'Chippie was disconnected from this Xero organisation. Reconnect to restore the link.',
            secret_rotated: 'Xero credentials were rotated. Reconnect to issue new tokens.',
            auth_401: 'Xero rejected the session token. Reconnect Xero to restore sync.',
            auth_403: 'Xero rejected the connection. The app may have been disconnected on the Xero side, or the connecting user removed from the organisation. Reconnect to restore sync.',
            network: 'Could not reach Xero on the last attempt. Check the Xero status page or try again in a few minutes.',
            unknown_auth_error: 'Xero returned an unexpected auth error. Reconnect to retry.',
        };
        // Prefer the server-supplied message (set by the pre-flight probe)
        // when present — it's already tailored to the exact failure mode.
        const msg = status.last_auth_error_message
            || msgByReason[reason]
            || msgByReason.unknown_auth_error;
        banner.className = 'alert alert-warning';
        banner.hidden = false;
        banner.textContent = msg;
        blurb.textContent = '';
        label.textContent = 'Reconnect Xero';
    }

    function renderStatus(status) {
        // status shape: {connected, state, xero_tenant_name, last_synced_at,
        //                deactivation_reason, last_auth_error_status, last_auth_error_detail,
        //                stats: {clients: {total, synced}, invoices: {total, synced}},
        //                pending_org_choice}
        hide('xero-loading');
        (status && status.pending_org_choice ? show : hide)('xero-choose-org');
        if (!status || !status.connected) {
            hide('xero-connected');
            renderDisconnectedState(status || {});
            show('xero-disconnected');
            return;
        }
        hide('xero-disconnected');
        show('xero-connected');
        setText('xero-org-name', status.xero_tenant_name || '—');
        setText('xero-last-sync', relativeTime(status.last_synced_at));
        const stats = status.stats || {};
        const clients = stats.clients || {};
        const invoices = stats.invoices || {};
        setText('xero-clients-synced', clients.synced != null ? clients.synced : 0);
        setText('xero-clients-total', clients.total != null ? clients.total : 0);
        setText('xero-invoices-synced', invoices.synced != null ? invoices.synced : 0);
        setText('xero-invoices-total', invoices.total != null ? invoices.total : 0);
        setText('xero-invoices-awaiting-payment',
            invoices.awaiting_payment_push != null ? invoices.awaiting_payment_push : 0);
        const awaitingRow = document.getElementById('xero-invoices-awaiting-payment-row');
        if (awaitingRow) {
            awaitingRow.hidden = !(invoices.awaiting_payment_push > 0);
        }

        // A connection can read "Connected" while its inbound path (webhook +
        // reconcile pull) is silently dead. Surface that distinctly from the
        // normal healthy state.
        setWarning('xero-inbound-warning', status.inbound_warning);
        // Likewise, it can read "Connected" with a pile of paid invoices Xero
        // still shows as awaiting payment.
        setWarning('xero-payment-warning', status.payment_warning);
    }

    // Show a server-built warning string, or hide the slot. The copy is the
    // service's to own; this only renders it.
    function setWarning(elementId, message) {
        const el = document.getElementById(elementId);
        if (!el) return;
        if (message) {
            el.textContent = message;
            el.hidden = false;
        } else {
            el.hidden = true;
            el.textContent = '';
        }
    }

    async function refreshStatus() {
        const result = await getJSON('/settings/xero/status');
        if (result.ok && result.data && result.data.success) {
            renderStatus(result.data.data);
            // Attach to a sync already running at mount (page reload / other tab)
            // straight from this payload, so resume needs no extra status GET.
            onBulkStatus(result.data.data);
            // Auto-load logs + mapping config once connected
            if (result.data.data && result.data.data.connected) {
                refreshLogs();
                refreshConfig().then(bindMappingSelectChange);
            }
        } else {
            hide('xero-loading');
            show('xero-disconnected');
        }
    }

    async function refreshLogs() {
        const result = await getJSON('/settings/xero/sync/logs?limit=20');
        if (result.ok && result.data && result.data.success) {
            renderLogs(result.data.logs || []);
        }
    }

    // ------------------------------------------------------------------------
    // Invoice mapping defaults (revenue + payment account codes)
    // ------------------------------------------------------------------------

    function populateMappingSelect(selectEl, options, currentValue, valueKey, labelFn) {
        if (!selectEl) return;
        // Preserve the existing first <option> (the placeholder)
        const placeholder = selectEl.options[0]
            ? selectEl.options[0].cloneNode(true) : null;
        selectEl.innerHTML = '';
        if (placeholder) selectEl.appendChild(placeholder);
        (options || []).forEach(function (opt) {
            const o = document.createElement('option');
            o.value = opt[valueKey] || '';
            o.textContent = labelFn(opt);
            if (currentValue && o.value === currentValue) o.selected = true;
            selectEl.appendChild(o);
        });
    }

    async function refreshConfig(forceRefreshFromXero) {
        const url = forceRefreshFromXero
            ? '/settings/xero/config?refresh=1'
            : '/settings/xero/config';
        const result = await getJSON(url);
        if (!(result.ok && result.data && result.data.success)) return;
        const cfg = result.data.data || {};
        populateMappingSelect(
            document.getElementById('xero-account-code'),
            cfg.available_accounts,
            cfg.default_account_code,
            'code',
            function (a) { return a.code + ' — ' + (a.name || ''); }
        );
        populatePaymentAccountSelect(
            document.getElementById('xero-payment-account'),
            cfg.available_payment_accounts,
            cfg.default_payment_account_id
        );
    }

    // Bank accounts first, the rest in their own group: an owner-equity account
    // can accept payments in Xero, and picking one files every customer
    // payment as owner drawings. Values are AccountIDs because a Xero bank
    // account usually has no code.
    function populatePaymentAccountSelect(selectEl, accounts, currentId) {
        if (!selectEl) return;
        const placeholder = selectEl.options[0] ? selectEl.options[0].cloneNode(true) : null;
        selectEl.innerHTML = '';
        if (placeholder) selectEl.appendChild(placeholder);
        const groups = [
            {label: 'Bank accounts', items: (accounts || []).filter(function (a) { return a.is_bank; })},
            {label: 'Other accounts (not a bank account)', items: (accounts || []).filter(function (a) { return !a.is_bank; })},
        ];
        groups.forEach(function (group) {
            if (!group.items.length) return;
            const optgroup = document.createElement('optgroup');
            optgroup.label = group.label;
            group.items.forEach(function (a) {
                const o = document.createElement('option');
                o.value = a.id;
                o.textContent = a.code ? a.code + ' — ' + (a.name || '') : (a.name || a.id);
                if (currentId && a.id === currentId) o.selected = true;
                optgroup.appendChild(o);
            });
            selectEl.appendChild(optgroup);
        });
    }

    // Bypass the 1h reference-data cache and re-fetch /Accounts
    // from Xero (tax treatment is region-derived, not configurable here).
    // Use after the user edited their chart of accounts and
    // wants the new entries to appear in the mapping selects immediately.
    window.xeroRefreshMappingOptions = async function () {
        const status = document.getElementById('xero-refresh-status');
        if (status) {
            status.textContent = 'Refreshing…';
            status.hidden = false;
            status.classList.remove('text-success', 'text-danger');
        }
        try {
            await refreshConfig(true);
            if (status) {
                status.textContent = 'Refreshed';
                status.classList.add('text-success');
                setTimeout(function () { status.hidden = true; }, 2000);
            }
        } catch (_e) {
            if (status) {
                status.textContent = 'Refresh failed';
                status.classList.add('text-danger');
            }
        }
    };

    window.xeroSaveConfig = async function () {
        const account = (document.getElementById('xero-account-code') || {}).value || '';
        const paymentAccount = (document.getElementById('xero-payment-account') || {}).value || '';
        const result = await postJSON('/settings/xero/config', {
            default_account_code: account,
            default_payment_account_id: paymentAccount,
        });
        const savedEl = document.getElementById('xero-config-saved');
        if (result.ok && result.data && result.data.success) {
            if (savedEl) {
                savedEl.hidden = false;
                setTimeout(function () { savedEl.hidden = true; }, 2000);
            }
            // Picking a payment account for the first time releases any paid
            // invoices that were stranded without one. Report what actually
            // happened: the server may have deferred the drain (no revenue
            // account yet) or found a sync already running, and claiming
            // "Syncing N" in those cases would be a lie the counts contradict.
            const backfill = (result.data.data || {}).payment_backfill;
            if (backfill && backfill.pending > 0) {
                const noun = backfill.pending === 1 ? 'invoice' : 'invoices';
                const count = backfill.pending + ' paid ' + noun;
                if (backfill.status === 'started') {
                    window.showSuccessMessage('Syncing ' + count + ' to Xero now.');
                } else if (backfill.status === 'needs_revenue_account') {
                    window.showSuccessMessage(
                        count + ' are waiting on a Xero payment. Choose a revenue account too and they will sync.'
                    );
                } else {
                    window.showSuccessMessage(
                        count + ' are waiting on a Xero payment; the next sync will push them.'
                    );
                }
            }
            refreshStatus();
        } else {
            const msg = (result.data && result.data.error) || 'Failed to save Xero config';
            window.showErrorMessage(msg);
        }
    };

    function bindMappingSelectChange() {
        ['xero-account-code', 'xero-payment-account'].forEach(function (id) {
            const el = document.getElementById(id);
            if (el && !el.dataset.changeBound) {
                el.addEventListener('change', window.xeroSaveConfig);
                el.dataset.changeBound = '1';
            }
        });
    }

    // ------------------------------------------------------------------------
    // Public handlers (data-fn targets)
    // ------------------------------------------------------------------------

    window.xeroConnect = async function () {
        const result = await postJSON('/settings/xero/connect');
        if (result.ok && result.data && result.data.auth_url) {
            window.location.href = result.data.auth_url;
        } else if (result.status === 402 && result.data.code === 'feature_not_available') {
            // The plan does not include Xero: offer the way to one that does,
            // except in the native shells, which may only give the reason.
            if (isNativeShell()) {
                window.showErrorMessage(result.data.error);
                return;
            }
            const panel = document.getElementById('xero-panel');
            if (window.confirm(result.data.error + '\n\nGo to Billing to upgrade?')) {
                window.Turbo.visit(panel.dataset.xeroBillingUrl);
            }
        } else {
            const msg = (result.data && (result.data.error || result.data.message)) || 'Failed to start Xero connection';
            window.showErrorMessage(msg);
        }
    };

    window.xeroDisconnect = async function () {
        const message = 'Disconnect Xero?\n\n' +
            'Chippie will stop pushing changes and will be removed from your ' +
            'Xero org’s Connected Apps. Your Xero data is unaffected, and ' +
            'Chippie’s sync history and contact links are kept so you can ' +
            'reconnect later.';
        if (!window.confirm(message)) return;
        const result = await postJSON('/settings/xero/disconnect');
        if (result.ok && result.data && result.data.success) {
            window.showSuccessMessage('Xero disconnected');
            await refreshStatus();
        } else {
            const msg = (result.data && result.data.error) || 'Failed to disconnect Xero';
            window.showErrorMessage(msg);
        }
    };

    function setProgress(pct, processed, pending) {
        const bar = document.getElementById('xero-sync-progress-bar');
        if (!bar) return;
        const clamped = Math.min(100, Math.max(0, pct));
        bar.style.width = clamped + '%';
        bar.setAttribute('aria-valuenow', String(Math.round(clamped)));
        bar.textContent = processed + ' / ' + pending;
    }

    function showProgressPanel(label, pending) {
        const panel = document.getElementById('xero-sync-progress');
        const labelEl = document.getElementById('xero-sync-progress-label');
        const bar = document.getElementById('xero-sync-progress-bar');
        const errWrap = document.getElementById('xero-sync-progress-errors-wrap');
        if (labelEl) labelEl.textContent = label;
        if (bar) bar.classList.add('progress-bar-animated');
        if (errWrap) errWrap.hidden = true;
        setProgress(0, 0, pending);
        if (panel) panel.hidden = false;
    }

    function finalizeProgressPanel(processed, pending, errors) {
        const bar = document.getElementById('xero-sync-progress-bar');
        const errWrap = document.getElementById('xero-sync-progress-errors-wrap');
        const errCount = document.getElementById('xero-sync-progress-errors');
        setProgress(100, processed, pending);
        if (bar) bar.classList.remove('progress-bar-animated');
        if (errors > 0) {
            if (errCount) errCount.textContent = String(errors);
            if (errWrap) errWrap.hidden = false;
        }
    }

    function hideProgressPanelDelayed() {
        setTimeout(function () {
            const panel = document.getElementById('xero-sync-progress');
            if (panel) panel.hidden = true;
        }, 2500);
    }

    // Route the bulk-sync outcome to the correct toast. Green for clean
    // success, red for any failure. For invoices, a total failure nearly
    // always means clients aren't synced yet — surface that as the fix.
    function notifyBulkResult(entity, label, synced, errors, skippedDueToAuth) {
        skippedDueToAuth = skippedDueToAuth || 0;
        if (skippedDueToAuth > 0) {
            // Xero rejected the connection mid-sync. Sync state now shows
            // "needs reauth" — point user at the Reconnect CTA.
            window.showErrorMessage(
                skippedDueToAuth + ' ' + label + ' not attempted — ' +
                'Xero connection needs reauth. Reconnect above to resume.'
            );
            return;
        }
        if (errors === 0 && synced > 0) {
            window.showSuccessMessage(synced + ' ' + label + ' synced');
            return;
        }
        if (errors === 0 && synced === 0) {
            window.showSuccessMessage('All ' + label + ' already up to date');
            return;
        }
        if (synced === 0 && errors > 0) {
            let msg = 'All ' + errors + ' ' + label + ' failed to sync.';
            if (entity === 'invoices') {
                msg += ' Sync clients first — invoices need a synced Xero contact.';
            } else {
                msg += ' See the activity log below for details.';
            }
            window.showErrorMessage(msg);
            return;
        }
        window.showErrorMessage(
            synced + ' of ' + (synced + errors) + ' ' + label +
            ' synced, ' + errors + ' failed. See the activity log below.'
        );
    }

    // ------------------------------------------------------------------------
    // Bulk sync — non-blocking via background thread + DB-persisted state
    // ------------------------------------------------------------------------

    // Bulk-sync progress arrives over the tenant's private Mercure topic, not
    // by polling. `bulkStream` is the page-scoped EventSource (opened on
    // connect, closed on disconnect). `bulkState` tracks the sync we are
    // attached to ({entity, label, baseline, sawInProgress}); null when idle.
    // It also serves as the double-start / double-attach guard the old single
    // poller (activePollId) used to provide.
    let bulkStream = null;
    let bulkState = null;
    // The EventSource 'open' fires on first connect AND every reconnect. The
    // first open needs no reconcile GET — refreshStatus() already read current
    // truth (and attaches to a running sync via onBulkStatus) on every mount.
    // Only genuine reconnects, which can have missed events while down, do.
    let streamOpened = false;

    function setSyncButtonsDisabled(disabled) {
        document.querySelectorAll('#xero-connected button').forEach(function (b) {
            b.disabled = disabled;
        });
    }

    function labelFor(entity) {
        if (entity === 'clients') return 'clients';
        if (entity === 'invoices') return 'invoices';
        if (entity === 'all') return 'clients and invoices';
        return entity || 'records';
    }

    // An invoice counts as synced only when it is BOTH linked to a Xero
    // invoice and paid there. Counting the link alone made a payment backfill
    // (every invoice already linked) start and finish at 100%. Mirrors
    // XeroSyncService._compute_baseline_synced; keep the two in step.
    function currentSyncedFor(data, entity) {
        const stats = (data && data.stats) || {};
        const c = (stats.clients || {}).synced || 0;
        const invoices = stats.invoices || {};
        const i = (invoices.synced || 0) - (invoices.awaiting_payment_push || 0);
        if (entity === 'clients') return c;
        if (entity === 'invoices') return i;
        if (entity === 'all') return c + i;
        return 0;
    }

    function totalFor(data, entity) {
        const stats = (data && data.stats) || {};
        const c = (stats.clients || {}).total || 0;
        const i = (stats.invoices || {}).total || 0;
        if (entity === 'clients') return c;
        if (entity === 'invoices') return i;
        if (entity === 'all') return c + i;
        return 0;
    }

    // Build the progress baseline. On resume, the server-persisted
    // baseline_synced is authoritative (captured at claim time); fall
    // back to the live stats snapshot when starting fresh.
    function buildBaseline(data, entity, serverBaseline) {
        const baselineSynced = serverBaseline != null
            ? serverBaseline
            : currentSyncedFor(data, entity);
        const total = totalFor(data, entity);
        return {
            total: total,
            baselineSynced: baselineSynced,
            pending: Math.max(0, total - baselineSynced),
        };
    }

    // Apply one status payload (delivered by a Mercure 'xero_bulk_progress'
    // event, or by the single reconcile GET on each (re)connect). The payload
    // is the same dict /settings/xero/status returns, so this drives the exact
    // render path the old poll did — minus the fetch and the setTimeout loop.
    //
    // First sighting of an in-progress sync attaches us to it (so a page-load
    // resume or a sync started in another tab is picked up). Completion is
    // edge-triggered on the in_progress true→false transition, guarded by a
    // prior in_progress=true sighting so a resume that lands after the sync
    // already finished never fires a spurious toast.
    function onBulkStatus(data) {
        if (!data) return;

        if (data.bulk_sync_in_progress && bulkState === null) {
            const e = data.bulk_sync_entity || 'all';
            bulkState = {
                entity: e,
                label: labelFor(e),
                baseline: buildBaseline(data, e, data.bulk_sync_baseline_synced),
                sawInProgress: true,
            };
            setSyncButtonsDisabled(true);
            showProgressPanel(bulkState.label, bulkState.baseline.pending);
        }
        if (bulkState === null) return;

        const entity = bulkState.entity;
        const baseline = bulkState.baseline;
        renderStatus(data);
        const processed = Math.max(0, currentSyncedFor(data, entity) - baseline.baselineSynced);
        const pct = baseline.pending > 0 ? (processed / baseline.pending) * 100 : 100;
        setProgress(pct, processed, baseline.pending);
        // Render the activity log straight from the event's recent_syncs so a
        // live update costs no extra query; the full detailed log is fetched
        // once on completion below.
        renderLogs(data.recent_syncs || []);

        if (data.bulk_sync_in_progress) {
            bulkState.sawInProgress = true;
            return;
        }
        if (!bulkState.sawInProgress) {
            return;
        }

        // Completion — clear tracking, finalise, then fetch the full log once.
        const st = bulkState;
        bulkState = null;
        const unprocessed = Math.max(0, st.baseline.pending - processed);
        // If the connection flipped to needs_reauth mid-sync, the unprocessed
        // remainder is auth-skipped, not "failed" — point the user at Reconnect.
        const skippedDueToAuth = (data && data.state === 'needs_reauth') ? unprocessed : 0;
        const errors = skippedDueToAuth > 0 ? 0 : unprocessed;
        finalizeProgressPanel(processed, st.baseline.pending, errors);
        notifyBulkResult(st.entity, st.label, processed, errors, skippedDueToAuth);
        setSyncButtonsDisabled(false);
        hideProgressPanelDelayed();
        refreshLogs();
        // No re-render here: renderStatus(data) already ran above for this same
        // payload, so a needs_reauth completion banner is already on screen.
    }

    // One reconcile GET against current truth. Runs on each stream (re)connect
    // to close the only gap a pushed stream has: a sync that completes while
    // the connection is down emits no event we can replay, so we re-read once
    // on reconnect. This is not a loop — it fires per connection, not on a
    // cadence.
    async function reconcileBulkStatus() {
        const result = await getJSON('/settings/xero/status');
        const data = result && result.data && result.data.data;
        if (data) onBulkStatus(data);
    }

    // Open the page-scoped EventSource to the tenant's private bulk-sync topic.
    // The mercureAuthorization cookie (minted per request for business
    // principals) authorises the topic; withCredentials sends it. Native
    // EventSource auto-reconnects and replays via Last-Event-ID, so we let the
    // browser + hub heal the connection rather than hand-rolling backoff.
    function connectBulkStream(el) {
        if (bulkStream) return;
        const hubUrl = el && el.dataset.xeroHubUrl;
        const topic = el && el.dataset.xeroTopic;
        if (!hubUrl || !topic) return;
        const sep = hubUrl.indexOf('?') === -1 ? '?' : '&';
        const url = hubUrl + sep + 'topic=' + encodeURIComponent(topic);
        const es = new EventSource(url, {withCredentials: true});
        es.addEventListener('xero_bulk_progress', function (e) {
            let data;
            try { data = JSON.parse(e.data); } catch (_err) { return; }
            onBulkStatus(data);
        });
        // 'open' fires on first connect AND every reconnect. Skip the first
        // (refreshStatus already reconciled on mount); reconcile only on a
        // genuine reconnect, which may have missed events while down.
        es.addEventListener('open', function () {
            if (!streamOpened) { streamOpened = true; return; }
            reconcileBulkStatus();
        });
        bulkStream = es;
    }

    function disconnectBulkStream() {
        if (bulkStream) {
            bulkStream.close();
            bulkStream = null;
        }
        // Next mount opens a fresh stream; its first open is again covered by
        // that mount's refreshStatus, so the skip-first-open must reset here.
        streamOpened = false;
    }

    async function startBulkSync(entity, label, entities) {
        if (bulkState !== null) return;
        entities = entities || [entity];

        const status = await getJSON('/settings/xero/status');
        const data = (status && status.data && status.data.data) || {};

        // If another tab or a previous session has a sync running, attach
        // to it instead of trying to start a new one.
        if (data.bulk_sync_in_progress) {
            onBulkStatus(data);
            return;
        }

        setSyncButtonsDisabled(true);
        const preBaseline = buildBaseline(data, entity, null);
        showProgressPanel(label, preBaseline.pending);

        const result = await postJSON('/settings/xero/sync/all', {entities: entities});

        if (result.status === 412 && result.data && result.data.error_code === 'revenue_account_required') {
            openRevenueAccountModal(result.data.accounts, {
                kind: 'bulk', entity: entity, label: label, entities: entities,
            });
            return;
        }

        if (result.status === 202 && result.data && result.data.status === 'started') {
            preBaseline.baselineSynced = result.data.baseline_synced != null
                ? result.data.baseline_synced
                : preBaseline.baselineSynced;
            preBaseline.pending = Math.max(0, preBaseline.total - preBaseline.baselineSynced);
            // Attach directly; the worker's pushed events drive progress from
            // here, and the panel + disabled buttons are already set above.
            bulkState = {entity: entity, label: label, baseline: preBaseline, sawInProgress: true};
            return;
        }

        if (result.status === 409 && result.data && result.data.status === 'aborted') {
            // Pre-flight probe rejected the run. The server has already
            // flipped the connection to needs_reauth if applicable —
            // re-read status and let the standard disconnected banner
            // render with the probe's tailored message.
            setSyncButtonsDisabled(false);
            hideProgressPanelDelayed();
            window.showErrorMessage(result.data.message || 'Xero sync aborted before start.');
            const fresh = await getJSON('/settings/xero/status');
            const fd = (fresh && fresh.data && fresh.data.data) || {};
            renderStatus(fd);
            return;
        }

        if (result.status === 409) {
            // Auto-attach: someone else raced in. Re-read status so we use
            // the actual running entity (may differ from what we posted).
            const fresh = await getJSON('/settings/xero/status');
            const fd = (fresh && fresh.data && fresh.data.data) || {};
            if (fd.bulk_sync_in_progress) {
                onBulkStatus(fd);
                return;
            }
            // Race lost: the sync completed between 409 and re-read.
            setSyncButtonsDisabled(false);
            hideProgressPanelDelayed();
            return;
        }

        setSyncButtonsDisabled(false);
        hideProgressPanelDelayed();
        const msg = (result.data && result.data.error) || ('Failed to start ' + label + ' sync');
        window.showErrorMessage(msg);
    }

    window.xeroSyncAll = function () { return startBulkSync('all', 'all', ['clients', 'invoices']); };
    window.xeroSyncAllClients = function () { return startBulkSync('clients', 'clients'); };
    window.xeroSyncAllInvoices = function () { return startBulkSync('invoices', 'invoices'); };

    window.xeroSyncInvoice = async function (id) {
        const result = await postJSON('/settings/xero/sync/invoice/' + encodeURIComponent(id));
        if (result.status === 412 && result.data && result.data.error_code === 'revenue_account_required') {
            openRevenueAccountModal(result.data.accounts, {kind: 'invoice', id: id});
            return;
        }
        if (result.status === 412 && result.data && result.data.error_code === 'payment_account_required') {
            // Pushing now would leave a paid invoice unpaid in Xero. Say so and
            // point at the one setting that fixes it.
            window.showErrorMessage(result.data.error + ' Set it in Settings, Integrations, Xero.');
            return;
        }
        if (result.ok && result.data && result.data.success) {
            // 202 queued: the push runs on a background thread so a Xero 429
            // can't pin a request connection. Its outcome arrives as a
            // "synced to Xero" toast, or as a bell notification naming the
            // problem, so this only confirms the push has started.
            window.showSuccessMessage('Syncing invoice to Xero…');
            setTimeout(refreshLogs, 2500);
        } else {
            const detail = result.data && (result.data.error || (result.data.data && result.data.data.error));
            window.showErrorMessage(detail || 'Invoice sync failed');
        }
    };

    // ------------------------------------------------------------------------
    // Revenue account modal — opens when a sync attempt returns 412
    // "revenue_account_required". User picks an account, we persist it via
    // /settings/xero/config, then retry whatever sync triggered the modal.
    // ------------------------------------------------------------------------

    let pendingSyncRetry = null;

    function openRevenueAccountModal(accounts, retry) {
        const select = document.getElementById('xero-modal-account-code');
        if (!select) {
            // Modal markup missing — degrade to a toast rather than wedging.
            window.showErrorMessage('Choose a default revenue account in the Xero settings above, then retry.');
            return;
        }
        // Reset select options to just the placeholder, then append available accounts.
        while (select.options.length > 1) select.remove(1);
        (accounts || []).forEach(function (a) {
            const opt = document.createElement('option');
            opt.value = a.code || '';
            opt.textContent = (a.code || '') + ' — ' + (a.name || '');
            select.appendChild(opt);
        });
        select.value = '';
        hideModalError();
        pendingSyncRetry = retry || null;
        // Re-enable any sync buttons disabled by the aborted attempt so the
        // user isn't locked out if they cancel the modal.
        setSyncButtonsDisabled(false);
        hideProgressPanelDelayed();
        showModal();
    }

    function showModal() {
        const el = document.getElementById('xero-account-required-modal');
        if (!el) return;
        if (window.bootstrap && window.bootstrap.Modal) {
            window.bootstrap.Modal.getOrCreateInstance(el).show();
        } else {
            // Minimal fallback if Bootstrap JS hasn't loaded.
            el.classList.add('show');
            el.style.display = 'block';
            el.removeAttribute('aria-hidden');
        }
    }

    function hideModal() {
        const el = document.getElementById('xero-account-required-modal');
        if (!el) return;
        if (window.bootstrap && window.bootstrap.Modal) {
            const inst = window.bootstrap.Modal.getInstance(el);
            if (inst) inst.hide();
        } else {
            el.classList.remove('show');
            el.style.display = 'none';
            el.setAttribute('aria-hidden', 'true');
        }
    }

    function showModalError(text) {
        const wrap = document.getElementById('xero-modal-error');
        const el = document.getElementById('xero-modal-error-text');
        if (el) el.textContent = text || '';
        if (wrap) wrap.hidden = false;
    }

    function hideModalError() {
        const wrap = document.getElementById('xero-modal-error');
        if (wrap) wrap.hidden = true;
    }

    window.xeroSaveAccountAndRetry = async function () {
        const select = document.getElementById('xero-modal-account-code');
        const code = (select && select.value) || '';
        if (!code) {
            showModalError('Pick an account to continue.');
            return;
        }
        const save = await postJSON('/settings/xero/config', {default_account_code: code});
        if (!(save.ok && save.data && save.data.success)) {
            showModalError((save.data && save.data.error) || 'Failed to save account selection.');
            return;
        }
        hideModal();
        // Keep the main settings select in sync so the user sees their choice.
        const mainSelect = document.getElementById('xero-account-code');
        if (mainSelect) mainSelect.value = code;

        const retry = pendingSyncRetry;
        pendingSyncRetry = null;
        if (!retry) return;
        if (retry.kind === 'bulk') {
            startBulkSync(retry.entity, retry.label, retry.entities);
        } else if (retry.kind === 'invoice') {
            window.xeroSyncInvoice(retry.id);
        }
    };

    window.xeroSyncClient = async function (id) {
        const result = await postJSON('/settings/xero/sync/client/' + encodeURIComponent(id));
        if (result.ok && result.data && result.data.success) {
            window.showSuccessMessage('Client synced to Xero');
            if (typeof refreshLogs === 'function') refreshLogs();
            return;
        }
        // 409 → name-collision. The server's `error` is already the
        // user-facing message ("…already linked to client X. Rename or
        // merge before retrying."). Render it verbatim so the user knows
        // exactly which other Chippie client owns the Xero contact.
        const data = result.data || {};
        const detail = data.error || (data.data && data.data.error) || 'Client sync failed';
        window.showErrorMessage(detail);
        if (typeof refreshLogs === 'function') refreshLogs();
    };

    window.xeroRefreshLogs = refreshLogs;

    // ------------------------------------------------------------------------
    // Query-param toasts (post-OAuth landing) + URL cleanup
    // ------------------------------------------------------------------------

    function flashFromQueryParams() {
        const params = new URLSearchParams(window.location.search);
        const xero = params.get('xero');
        const error = params.get('error');
        const message = params.get('message');
        let consumed = false;

        if (xero === 'connected') {
            window.showSuccessMessage('Xero connected');
            consumed = true;
        }
        if (error) {
            const errorMap = {
                auth_failed: 'Xero authorization failed. Please try again.',
                missing_params: 'Xero callback was missing required parameters.',
                session_expired: 'Your Xero connection session expired. Please try again.',
                unknown: 'An unexpected error occurred connecting Xero.',
            };
            const text = errorMap[error] || message || ('Xero error: ' + error);
            window.showErrorMessage(text);
            consumed = true;
        }

        if (consumed) {
            // Strip the OAuth-result params but preserve ?tab=integrations
            params.delete('xero');
            params.delete('error');
            params.delete('message');
            const qs = params.toString();
            const newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
            window.history.replaceState({}, '', newUrl);
        }
    }

    // ------------------------------------------------------------------------
    // Page-load init (formerly attachOnce('settingsXeroInit') + turbo:load).
    // ------------------------------------------------------------------------

    function initXeroPanel() {
        if (!document.getElementById('xero-panel')) return;
        // Turbo replaces <body> but the IIFE closure survives on window. A
        // stale bulkState from a prior panel mount would make a new sync's
        // start guard early-exit; clear it so the fresh mount starts clean.
        // (The stream is re-opened in connect(); resume is handled by its
        // on-connect reconcile, not here.)
        bulkState = null;
        flashFromQueryParams();
        refreshStatus();
    }

    // ------------------------------------------------------------------------
    // CONTROLLER
    // ------------------------------------------------------------------------

    export default class extends Controller {
        connect() {
            // connect() runs on every navigation that inserts #xero-panel, so it
            // subsumes both the old once-only init and its turbo:load re-run.
            initXeroPanel();
            // Open the bulk-sync progress stream; its on-connect reconcile
            // attaches to any sync already running (resume / other tab).
            connectBulkStream(this.element);
        }

        disconnect() {
            // Close the stream so it doesn't outlive the panel's removal.
            disconnectBulkStream();
        }
    }
